import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Box, Button, Chip, Drawer, IconButton, Tab, Tabs, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import HandshakeIcon from "@mui/icons-material/Handshake";
import PersonIcon from "@mui/icons-material/Person";
import StoreIcon from "@mui/icons-material/Store";
import CampaignIcon from "@mui/icons-material/Campaign";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import {
  STATUS_META,
  BRAND_COLOR,
  formatLeadDateTime,
  formatCurrency,
  formatDuration,
  isReportableStatus,
  getScoreMeta,
  getQualifiedMeta,
  SCORING_STATUS_META,
  REPORT_LOG_STATUS_META,
} from "../constants";

interface LeadDetailDrawerProps {
  data: LeadsManagementData;
}

type DetailTabKey = "details" | "scoring" | "log";

function formatShortDate(iso: string): string {
  const date = new Date(iso);
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function SectionCard({ title, icon, fields }: { title: string; icon: ReactNode; fields: Array<[string, string]> }) {
  return (
    <Box sx={{ bgcolor: "#fff", border: "1px solid #ececec", borderRadius: 2.5, px: 2.25, pb: 1.25 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 1.75, pb: 0.75 }}>
        {icon}
        <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.5px", color: BRAND_COLOR }}>
          {title}
        </Typography>
      </Box>
      {fields.map(([label, value]) => (
        <Box
          key={label}
          sx={{
            display: "grid",
            gridTemplateColumns: "42% 58%",
            gap: 1.5,
            py: 1.25,
            borderTop: "1px solid #f4f4f4",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontSize: 13.5, color: "#8a8a8a" }}>{label}</Typography>
          <Typography sx={{ fontSize: 14, color: "#212121", wordBreak: "break-all" }}>{value}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function EmptyState({ icon, message }: { icon: ReactNode; message: string }) {
  return (
    <Box sx={{ bgcolor: "#fff", border: "1px solid #ececec", borderRadius: 2.5, p: "44px 20px", textAlign: "center" }}>
      {icon}
      <Typography sx={{ fontSize: 14.5, color: "#757575", mt: 1 }}>{message}</Typography>
    </Box>
  );
}

function ScoreGauge({
  title,
  score,
  bandSuffix,
}: {
  title: string;
  score: number;
  bandSuffix: string;
}) {
  const meta = getScoreMeta(score);
  return (
    <Box sx={{ flex: 1, bgcolor: "#fff", border: "1px solid #ececec", borderRadius: 2.5, p: 2.25 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.5px", color: "#8a8a8a" }}>
        {title}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mt: 1 }}>
        <Typography sx={{ fontSize: 38, fontWeight: 700, color: meta.color }}>{score.toFixed(1)}</Typography>
        <Typography sx={{ fontSize: 15, color: "#bdbdbd" }}>/ 10</Typography>
      </Box>
      <Box sx={{ height: 8, borderRadius: 2.5, bgcolor: "#f0f0f2", mt: 1.25, overflow: "hidden" }}>
        <Box sx={{ height: "100%", borderRadius: 2.5, bgcolor: meta.color, width: `${score * 10}%` }} />
      </Box>
      <Typography sx={{ fontSize: 12.5, color: "#757575", mt: 1 }}>
        {meta.band} {bandSuffix}
      </Typography>
    </Box>
  );
}

export default function LeadDetailDrawer({ data }: LeadDetailDrawerProps) {
  const {
    detailLead,
    closeLeadDetail,
    reportSingleLead,
    isReporting,
    canReportLeads,
    goToFilteredLeads,
  } = data;
  const [activeTab, setActiveTab] = useState<DetailTabKey>("details");

  // The drawer stays mounted across different leads (only `detailLead`
  // changes), so without this the tab you were on for the previous lead
  // would stick around when opening a different one.
  useEffect(() => {
    setActiveTab("details");
  }, [detailLead?.id]);

  if (!detailLead) return null;

  const meta = STATUS_META[detailLead.status];
  const qualifiedMeta = getQualifiedMeta(detailLead.qualifiedByBrand);
  const reportable = isReportableStatus(detailLead.status);
  const notReportableMessage =
    detailLead.status === "Not_Supported"
      ? "This lead cannot be reported — brand not supported."
      : detailLead.status === "Success"
      ? "This lead has already been reported successfully."
      : "This lead is not reportable.";

  const sections: Array<{ title: string; icon: ReactNode; fields: Array<[string, string]> }> = [
    {
      title: "BRAND OUTCOME",
      icon: <HandshakeIcon sx={{ fontSize: 19, color: BRAND_COLOR }} />,
      fields: [
        ["Brand lead status:", detailLead.brandLeadStatus ?? "—"],
        ["Qualified by brand:", qualifiedMeta.label],
        ["Income from brand:", formatCurrency(detailLead.incomeFromBrand)],
        ["Reported at:", formatLeadDateTime(detailLead.reportedAt)],
        ["Report attempts:", String(detailLead.reportLog.length)],
      ],
    },
    {
      title: "CONTACT",
      icon: <PersonIcon sx={{ fontSize: 19, color: BRAND_COLOR }} />,
      fields: [
        ["Full name:", detailLead.name],
        ["Email:", detailLead.email],
        ["Phone:", detailLead.phone],
        ["Zip code:", detailLead.zip],
      ],
    },
    {
      title: "BUSINESS & LOAN",
      icon: <StoreIcon sx={{ fontSize: 19, color: BRAND_COLOR }} />,
      fields: [
        ["Business name:", detailLead.businessName],
        ["Business industry:", detailLead.industry],
        ["Time in business:", detailLead.timeInBusiness],
        ["Monthly revenue:", detailLead.revenue],
        ["Credit rating:", detailLead.credit],
        ["Borrow amount:", detailLead.amount],
        ["Loan purpose:", detailLead.loanPurpose],
      ],
    },
    {
      title: "ATTRIBUTION",
      icon: <CampaignIcon sx={{ fontSize: 19, color: BRAND_COLOR }} />,
      fields: [
        ["User unique ID:", detailLead.userUniqueId],
        ["Traffic source:", detailLead.trafficSource],
        ["Ad ID:", detailLead.adId],
        ["Ad platform click ID:", detailLead.adPlatformClickId],
        ["Date of acquisition:", formatLeadDateTime(detailLead.date)],
      ],
    },
  ];

  // duplicate_ip_leads is scoped to the entire brand_leads table by the API
  // (not just the currently loaded page), and already excludes the lead
  // itself — see mapApiLeadToLead in constants.ts.
  const ipDupes = detailLead.duplicateIpLeads;

  const scoringStatus = detailLead.scoringStatus;
  const hasScoring =
    scoringStatus === "completed" &&
    detailLead.leadScore !== null &&
    detailLead.approvalLikelihood !== null;
  const noScoringMessage =
    scoringStatus === "processing"
      ? "Scoring is still processing for this lead — check back shortly."
      : scoringStatus === "failed"
        ? "The scoring run failed for this lead. No score is available."
        : "No scoring data is available for this lead yet.";

  const reportLog = detailLead.reportLog;

  const tabStyles = {
    minHeight: 0,
    "& .MuiTab-root": {
      minHeight: 0,
      py: 0.5,
      fontSize: 13,
      fontWeight: 500,
      letterSpacing: "0.5px",
      color: "#6b6b6b",
    },
    "& .Mui-selected": { color: `${BRAND_COLOR} !important` },
    "& .MuiTabs-indicator": { backgroundColor: BRAND_COLOR, height: 3 },
  };

  return (
    <Drawer
      anchor="right"
      open
      onClose={closeLeadDetail}
      sx={{ zIndex: (theme) => theme.zIndex.appBar + 200 }}
      PaperProps={{
        sx: { width: 640, maxWidth: "96vw", zIndex: (theme) => theme.zIndex.appBar + 200 },
      }}
      slotProps={{ backdrop: { onClick: closeLeadDetail } }}
    >
      <Box sx={{ p: "20px 26px 0", borderBottom: "1px solid #ececec" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 19, fontWeight: 700, color: "#1a1a1a" }}>
              {detailLead.name}{" "}
              <Box component="span" sx={{ color: "#bdbdbd", fontWeight: 400 }}>
                · #{detailLead.id}
              </Box>
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: "#757575", mt: 0.375 }}>{detailLead.brand}</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.25 }}>
              <Chip label={meta.label} size="small" sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 500 }} />
              <Chip
                label={qualifiedMeta.label}
                size="small"
                sx={{ bgcolor: qualifiedMeta.bg, color: qualifiedMeta.color, fontWeight: 500 }}
              />
            </Box>
          </Box>
          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
            <Typography sx={{ fontSize: 11.5, color: "#9e9e9e", letterSpacing: "0.4px" }}>
              INCOME FROM BRAND
            </Typography>
            <Typography
              sx={{
                fontSize: 26,
                fontWeight: 700,
                color: detailLead.incomeFromBrand ? "#2E7D32" : "#9e9e9e",
              }}
            >
              {formatCurrency(detailLead.incomeFromBrand)}
            </Typography>
          </Box>
          <IconButton onClick={closeLeadDetail} sx={{ mt: -0.5 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={{ mt: 1.5, ...tabStyles }}>
          <Tab value="details" label="DETAILS" />
          <Tab value="scoring" label="SCORING" data-tracker="966" />
          <Tab value="log" label={`REPORT LOG (${reportLog.length})`} data-tracker="967" />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", bgcolor: "#fafafa" }}>
        {activeTab === "details" && (
          <Box sx={{ p: "18px 26px 26px", display: "flex", flexDirection: "column", gap: 2 }}>
            {sections.map((section) => (
              <SectionCard key={section.title} title={section.title} icon={section.icon} fields={section.fields} />
            ))}

            <Box sx={{ bgcolor: "#fff", border: "1px solid #ececec", borderRadius: 2.5, px: 2.25, pb: 2, pt: 1.75 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.5px", color: BRAND_COLOR }}>
                  IP ADDRESS
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "42% 58%",
                  gap: 1.5,
                  py: 1.25,
                  borderTop: "1px solid #f4f4f4",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: 13.5, color: "#8a8a8a" }}>IP address:</Typography>
                <Typography sx={{ fontSize: 14, color: "#212121", fontFamily: "'Roboto Mono', monospace" }}>
                  {detailLead.ipAddress ?? "—"}
                </Typography>
              </Box>
              {ipDupes.length > 0 ? (
                <Box sx={{ pt: 1.5, mt: 0.25, borderTop: "1px solid #f4f4f4" }}>
                  <Typography sx={{ fontSize: 13.5, color: "#8a8a8a", mb: 1.125 }}>
                    {ipDupes.length} other lead{ipDupes.length === 1 ? "" : "s"} submitted from this IP:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {ipDupes.map((dup) => (
                      <Box
                        key={dup.id}
                        onClick={() => goToFilteredLeads({ search: String(dup.id) })}
                        title="Search for this lead by ID"
                        data-tracker="964"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          border: "1px solid #f0e3f5",
                          bgcolor: "#faf5fc",
                          borderRadius: 2.25,
                          px: 1.5,
                          py: 1,
                          cursor: "pointer",
                        }}
                      >
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: BRAND_COLOR }}>
                          #{dup.id}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "#616161" }}>{dup.fullName}</Typography>
                        <Typography sx={{ fontSize: 12, color: "#9e9e9e" }}>
                          {formatShortDate(dup.created)}
                        </Typography>
                        <OpenInNewIcon sx={{ fontSize: 16, color: BRAND_COLOR }} />
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
                    <Typography
                      onClick={() => detailLead.ipAddress && goToFilteredLeads({ search: detailLead.ipAddress })}
                      data-tracker="965"
                      sx={{ fontSize: 13, fontWeight: 500, color: BRAND_COLOR, cursor: "pointer" }}
                    >
                      See all leads from this IP →
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography sx={{ pt: 1.5, mt: 0.25, borderTop: "1px solid #f4f4f4", fontSize: 13.5, color: "#9e9e9e" }}>
                  No other lead was submitted from this IP address.
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {activeTab === "scoring" && (
          <Box sx={{ p: "18px 26px 26px", display: "flex", flexDirection: "column", gap: 2 }}>
            {hasScoring ? (
              <>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <ScoreGauge title="LEAD SCORING" score={detailLead.leadScore!} bandSuffix="lead quality" />
                  <ScoreGauge
                    title="LOAN APPROVAL LIKELIHOOD"
                    score={detailLead.approvalLikelihood!}
                    bandSuffix="approval likelihood"
                  />
                </Box>
                <Box sx={{ bgcolor: "#fff", border: "1px solid #ececec", borderRadius: 2.5, p: 2.25 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.5px", color: BRAND_COLOR, mb: 1.25 }}>
                    LEAD SCORING RATIONALE
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#333", lineHeight: 1.62, whiteSpace: "pre-wrap" }}>
                    {detailLead.scoreRationale}
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: "#fff", border: "1px solid #ececec", borderRadius: 2.5, p: 2.25 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.5px", color: BRAND_COLOR, mb: 1.25 }}>
                    LOAN APPROVAL LIKELIHOOD RATIONALE
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#333", lineHeight: 1.62, whiteSpace: "pre-wrap" }}>
                    {detailLead.approvalRationale}
                  </Typography>
                </Box>
              </>
            ) : (
              <EmptyState icon={<HourglassEmptyIcon sx={{ fontSize: 38, color: "#d6c2de" }} />} message={noScoringMessage} />
            )}

            <Box sx={{ bgcolor: "#fff", border: "1px solid #ececec", borderRadius: 2.5, p: 2.25 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.5px", color: BRAND_COLOR }}>
                  SCORING RUN
                </Typography>
                {scoringStatus && (
                  <Chip
                    label={SCORING_STATUS_META[scoringStatus].label}
                    size="small"
                    sx={{
                      bgcolor: SCORING_STATUS_META[scoringStatus].bg,
                      color: SCORING_STATUS_META[scoringStatus].color,
                      fontWeight: 500,
                    }}
                  />
                )}
                <Typography sx={{ ml: "auto", fontSize: 12.5, color: "#9e9e9e" }}>
                  Updated {formatLeadDateTime(detailLead.scoringUpdatedAt ?? null)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "42% 58%",
                  gap: 1.5,
                  pt: 1.75,
                  mt: 1.75,
                  borderTop: "1px solid #f4f4f4",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: 13.5, color: "#8a8a8a" }}>Questionnaire fill time:</Typography>
                <Typography sx={{ fontSize: 14, color: "#212121" }}>
                  {formatDuration(detailLead.questionnaireFillTimeSeconds)}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {activeTab === "log" && (
          <Box sx={{ p: "18px 26px 26px", display: "flex", flexDirection: "column", gap: 1.75 }}>
            {reportLog.length === 0 ? (
              <EmptyState
                icon={<ReceiptLongIcon sx={{ fontSize: 38, color: "#d6c2de" }} />}
                message="No report attempts recorded for this lead yet."
              />
            ) : (
              reportLog.map((log) => {
                const logMeta = REPORT_LOG_STATUS_META[log.status];
                return (
                  <Box key={log.id} sx={{ bgcolor: "#fff", border: "1px solid #ececec", borderRadius: 2.5, p: "16px 18px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: logMeta.color, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212121" }}>
                        Attempt #{log.attempt}
                      </Typography>
                      <Chip
                        label={logMeta.label}
                        size="small"
                        sx={{ bgcolor: logMeta.bg, color: logMeta.color, fontWeight: 500 }}
                      />
                      <Typography sx={{ ml: "auto", fontSize: 12.5, color: "#9e9e9e" }}>
                        {formatLeadDateTime(log.createdAt)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 11.5, color: "#9e9e9e", mt: 1.5, letterSpacing: "0.4px" }}>
                      RESPONSE PAYLOAD · log id {log.id}
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        mt: 0.75,
                        bgcolor: "#faf7fb",
                        border: "1px solid #f0e6f4",
                        borderRadius: 1.5,
                        p: "12px 14px",
                        fontFamily: "'Roboto Mono', monospace",
                        fontSize: 12,
                        lineHeight: 1.55,
                        color: "#4a4a4a",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                        maxHeight: 200,
                        overflow: "auto",
                      }}
                    >
                      {log.payload}
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ p: "18px 24px", borderTop: "1px solid #ececec" }}>
        {reportable ? (
          <Button
            fullWidth
            variant="contained"
            startIcon={<SendIcon />}
            disabled={!canReportLeads || isReporting}
            onClick={() => reportSingleLead(detailLead.id)}
            data-tracker="930"
            sx={{ bgcolor: BRAND_COLOR, "&:hover": { bgcolor: "#7B1FA2" }, py: 1.4, letterSpacing: "0.4px" }}
          >
            {isReporting ? "Reporting…" : "Report to brand"}
          </Button>
        ) : (
          <Typography sx={{ textAlign: "center", fontSize: 13.5, color: "#9e9e9e" }}>
            {notReportableMessage}
          </Typography>
        )}
      </Box>
    </Drawer>
  );
}
